/**
 * 通用轮询工具函数
 * 用于轮询任务状态直到完成
 */

import type { TaskStatusResponse, PollingOptions } from '~/types'
import { TaskStatus } from '~/types'

/**
 * 轮询任务状态
 * @param taskId 任务ID
 * @param getStatusFn 获取任务状态的函数
 * @param options 轮询配置选项
 * @returns Promise<TaskStatusResponse> 最终的任务状态
 */
export async function pollTaskStatus(
    taskId: string,
    getStatusFn: (taskId: string) => Promise<TaskStatusResponse>,
    options: PollingOptions = {}
): Promise<TaskStatusResponse> {
    const {
        interval = 5000, // 默认5秒
        maxAttempts = 360, // 默认360次（30分钟），防止内存泄漏。原1000次会导致1.5小时轮询
        onProgress,
        onSuccess,
        onError,
        onTimeout,
    } = options

    let attempts = 0
    let timeoutId: NodeJS.Timeout | null = null

    return new Promise((resolve, reject) => {
        const poll = async () => {
            try {
                attempts++

                // 获取任务状态
                const response: any = await getStatusFn(taskId)

                // 触发进度回调
                if (onProgress) {
                    onProgress(response)
                }

                // 检查任务是否完成
                if (response.status === TaskStatus.SUCCESS) {
                    if (timeoutId) {
                        clearTimeout(timeoutId)
                    }
                    if (onSuccess) {
                        onSuccess(response)
                    }
                    resolve(response)
                    return
                }

                // 检查任务是否失败
                if (
                    [
                        TaskStatus.FAILED,
                        TaskStatus.EXCEPTION,
                        TaskStatus.TIMEOUT,
                        TaskStatus.CANCELLED,
                    ].includes(response.status)
                ) {
                    if (timeoutId) {
                        clearTimeout(timeoutId)
                    }
                    const errorMsg = response.error_message
                    if (onError) {
                        onError(errorMsg)
                    }
                    reject(new Error(errorMsg))
                    return
                }

                // 检查是否超过最大尝试次数（如果设置了限制）
                if (maxAttempts !== null && attempts >= maxAttempts) {
                    if (timeoutId) {
                        clearTimeout(timeoutId)
                    }
                    if (onTimeout) {
                        onTimeout()
                    }
                    reject(
                        new Error(
                            `轮询超时: 已尝试 ${attempts} 次，超过最大限制 ${maxAttempts} 次`
                        )
                    )
                    return
                }

                // 继续轮询
                timeoutId = setTimeout(poll, interval)
            } catch (error) {
                if (timeoutId) {
                    clearTimeout(timeoutId)
                }
                const errorMsg =
                    error instanceof Error
                        ? error.message
                        : '轮询过程中发生未知错误'
                if (onError) {
                    onError(errorMsg)
                }
                reject(error)
            }
        }

        // 开始轮询
        poll()
    })
}

/**
 * 创建轮询器类
 * 提供更灵活的任务轮询管理
 */
export class TaskPoller {
    private taskId: string
    private getStatusFn: (taskId: string) => Promise<TaskStatusResponse>
    private options: PollingOptions
    private timeoutId: NodeJS.Timeout | null = null
    private isPolling = false
    private attempts = 0

    constructor(
        taskId: string,
        getStatusFn: (taskId: string) => Promise<TaskStatusResponse>,
        options: PollingOptions = {}
    ) {
        this.taskId = taskId
        this.getStatusFn = getStatusFn
        this.options = {
            interval: 5000,
            maxAttempts: 120, // 默认120次，null表示不限制
            ...options,
        }
    }

    /**
     * 开始轮询
     */
    async start(): Promise<TaskStatusResponse> {
        if (this.isPolling) {
            throw new Error('轮询器已在运行中')
        }

        this.isPolling = true
        this.attempts = 0

        return pollTaskStatus(this.taskId, this.getStatusFn, {
            ...this.options,
            onProgress: status => {
                this.attempts++
                this.options.onProgress?.(status)
            },
            onSuccess: result => {
                this.isPolling = false
                this.options.onSuccess?.(result)
            },
            onError: error => {
                this.isPolling = false
                this.options.onError?.(error)
            },
            onTimeout: () => {
                this.isPolling = false
                this.options.onTimeout?.()
            },
        })
    }

    /**
     * 停止轮询
     */
    stop() {
        if (this.timeoutId) {
            clearTimeout(this.timeoutId)
            this.timeoutId = null
        }
        this.isPolling = false
    }

    /**
     * 检查是否正在轮询
     */
    get polling() {
        return this.isPolling
    }

    /**
     * 获取当前尝试次数
     */
    get currentAttempts() {
        return this.attempts
    }
}

/**
 * 批量轮询多个任务
 * @param tasks 任务列表
 * @param getStatusFn 获取任务状态的函数
 * @param options 轮询配置选项
 * @returns Promise<TaskStatusResponse[]> 所有任务的最终状态
 */
export async function pollMultipleTasks(
    tasks: string[],
    getStatusFn: (taskId: string) => Promise<TaskStatusResponse>,
    options: PollingOptions = {}
): Promise<TaskStatusResponse[]> {
    const promises = tasks.map(taskId =>
        pollTaskStatus(taskId, getStatusFn, options)
    )

    return Promise.all(promises)
}

/**
 * 轮询任务直到成功（忽略失败的任务）
 * @param tasks 任务列表
 * @param getStatusFn 获取任务状态的函数
 * @param options 轮询配置选项
 * @returns Promise<TaskStatusResponse[]> 成功完成的任务状态
 */
export async function pollTasksUntilSuccess(
    tasks: string[],
    getStatusFn: (taskId: string) => Promise<TaskStatusResponse>,
    options: PollingOptions = {}
): Promise<TaskStatusResponse[]> {
    const results: TaskStatusResponse[] = []
    const remainingTasks = [...tasks]

    while (remainingTasks.length > 0) {
        const promises = remainingTasks.map(async taskId => {
            try {
                return await pollTaskStatus(taskId, getStatusFn, options)
            } catch (error) {
                console.warn(`任务 ${taskId} 轮询失败:`, error)
                return null
            }
        })

        const settledResults = await Promise.allSettled(promises)

        // 处理成功的结果
        settledResults.forEach((result, index) => {
            if (result.status === 'fulfilled' && result.value) {
                results.push(result.value)
                // 从剩余任务中移除已完成的任务
                const taskId = remainingTasks[index]
                if (taskId) {
                    const taskIndex = remainingTasks.indexOf(taskId)
                    if (taskIndex > -1) {
                        remainingTasks.splice(taskIndex, 1)
                    }
                }
            }
        })

        // 如果还有剩余任务，等待一段时间后继续
        if (remainingTasks.length > 0) {
            await new Promise(resolve =>
                setTimeout(resolve, options.interval || 5000)
            )
        }
    }

    return results
}
